import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getBotState } from '../services/api';

interface BotStateContextType {
  isActive: boolean;
  maintenanceMessage: string;
  isLoading: boolean;
  error: string | null;
  refreshBotState: () => Promise<void>;
}

const BotStateContext = createContext<BotStateContextType | undefined>(undefined);

interface BotStateProviderProps {
  children: ReactNode;
}

export const BotStateProvider: React.FC<BotStateProviderProps> = ({ children }) => {
  const [isActive, setIsActive] = useState<boolean>(true);
  const [maintenanceMessage, setMaintenanceMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadBotState = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const botState = await getBotState();
      setIsActive(botState.isActive);
      setMaintenanceMessage(botState.maintenanceMessage);
    } catch (err) {
      console.error('Error loading bot state:', err);
      setError('Failed to load bot state');
      // Default to active in case of error
      setIsActive(true);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshBotState = async () => {
    await loadBotState();
  };

  useEffect(() => {
    loadBotState();
  }, []);

  const value = {
    isActive,
    maintenanceMessage,
    isLoading,
    error,
    refreshBotState
  };

  return <BotStateContext.Provider value={value}>{children}</BotStateContext.Provider>;
};

export const useBotState = (): BotStateContextType => {
  const context = useContext(BotStateContext);
  if (context === undefined) {
    throw new Error('useBotState must be used within a BotStateProvider');
  }
  return context;
};
